import React, { useState, useEffect } from 'react';
import {
    X, Clock, Video, Phone, MapPin, Link2, Loader2, Palette
} from 'lucide-react';
import { motion } from 'framer-motion';
import { createEventType, updateEventType } from '../api/calendar';

const LOCATION_TYPES = [
    { id: 'video', label: 'Video Call', icon: Video, description: 'Zoom, Google Meet, etc.' },
    { id: 'phone', label: 'Phone Call', icon: Phone, description: 'Phone number provided' },
    { id: 'in_person', label: 'In Person', icon: MapPin, description: 'Physical location' },
    { id: 'custom', label: 'Custom', icon: Link2, description: 'Custom location/link' }
];

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

const COLOR_OPTIONS = [
    '#3B82F6', // Blue
    '#10B981', // Emerald
    '#8B5CF6', // Purple
    '#fbbf24', // Yellow
    '#EF4444', // Red
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#84CC16'  // Lime
];

const EventTypeEditor = ({ eventType, schedules = [], onSave, onClose }) => {
    const isEditing = !!eventType?.id;

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        description: '',
        duration: 30,
        locationType: 'video',
        location: '',
        color: '#3B82F6',
        isActive: true,
        requiresConfirmation: false,
        bufferBefore: 0,
        bufferAfter: 0,
        minNotice: 60,
        maxBookingsPerDay: null,
        scheduleId: ''
    });

    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (eventType) {
            setFormData({
                title: eventType.title || '',
                slug: eventType.slug || '',
                description: eventType.description || '',
                duration: eventType.duration || 30,
                locationType: eventType.location_type || 'video',
                location: eventType.location || '',
                color: eventType.color || '#3B82F6',
                isActive: eventType.is_active !== false,
                requiresConfirmation: eventType.requires_confirmation || false,
                bufferBefore: eventType.buffer_before || 0,
                bufferAfter: eventType.buffer_after || 0,
                minNotice: eventType.min_notice || 60,
                maxBookingsPerDay: eventType.max_bookings_per_day || null,
                scheduleId: eventType.schedule_id || ''
            });
        }
    }, [eventType]);

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setFormData(prev => ({
            ...prev,
            title,
            slug: prev.slug || generateSlug(title)
        }));
        if (errors.title) {
            setErrors(prev => ({ ...prev, title: null }));
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        if (!formData.duration || formData.duration < 5) {
            newErrors.duration = 'Duration must be at least 5 minutes';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSaving(true);
        try {
            const payload = {
                title: formData.title.trim(),
                slug: formData.slug || generateSlug(formData.title),
                description: formData.description.trim() || null,
                duration: formData.duration,
                location_type: formData.locationType,
                location: formData.location.trim() || null,
                color: formData.color,
                is_active: formData.isActive,
                requires_confirmation: formData.requiresConfirmation,
                buffer_before: formData.bufferBefore,
                buffer_after: formData.bufferAfter,
                min_notice: formData.minNotice,
                max_bookings_per_day: formData.maxBookingsPerDay || null,
                schedule_id: formData.scheduleId || null
            };

            let result;
            if (isEditing) {
                result = await updateEventType(eventType.id, payload);
            } else {
                result = await createEventType(payload);
            }

            onSave?.(result.data);
        } catch (err) {
            console.error('Failed to save event type:', err);
            setErrors({ submit: err.message });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 dark:bg-slate-950/80 bg-slate-900/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-900 bg-white rounded-2xl shadow-2xl"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 px-6 py-4 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900 bg-white rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold dark:text-white text-slate-900">
                            {isEditing ? 'Edit Event Type' : 'New Event Type'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg dark:text-slate-400 text-slate-500 dark:hover:bg-slate-800 hover:bg-slate-100 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Title & Slug */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={handleTitleChange}
                                placeholder="e.g., 30 Minute Meeting"
                                className={`w-full px-4 py-2.5 rounded-lg border dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition ${
                                    errors.title ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                                }`}
                            />
                            {errors.title && (
                                <p className="text-sm text-red-500 mt-1">{errors.title}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                URL Slug
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm dark:text-slate-500 text-slate-400">
                                    /book/
                                </span>
                                <input
                                    type="text"
                                    value={formData.slug}
                                    onChange={(e) => handleChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                    placeholder="30-minute-meeting"
                                    className="w-full pl-14 pr-4 py-2.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="A brief description of this event type..."
                            rows={3}
                            className="w-full px-4 py-2.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition resize-none"
                        />
                    </div>

                    {/* Duration & Color */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                Duration *
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {DURATION_OPTIONS.map(duration => (
                                    <button
                                        key={duration}
                                        type="button"
                                        onClick={() => handleChange('duration', duration)}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                                            formData.duration === duration
                                                ? 'bg-yellow-500 text-white'
                                                : 'dark:bg-slate-800 bg-slate-100 dark:text-slate-300 text-slate-600 hover:bg-yellow-500/20'
                                        }`}
                                    >
                                        {duration}m
                                    </button>
                                ))}
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <input
                                    type="number"
                                    value={formData.duration}
                                    onChange={(e) => handleChange('duration', parseInt(e.target.value) || 30)}
                                    min={5}
                                    max={480}
                                    className="w-20 px-3 py-1.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                                />
                                <span className="text-sm dark:text-slate-400 text-slate-500">minutes</span>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                Color
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {COLOR_OPTIONS.map(color => (
                                    <button
                                        key={color}
                                        type="button"
                                        onClick={() => handleChange('color', color)}
                                        className={`w-8 h-8 rounded-full transition-transform ${
                                            formData.color === color ? 'ring-2 ring-offset-2 dark:ring-offset-slate-900 ring-yellow-500 scale-110' : ''
                                        }`}
                                        style={{ backgroundColor: color }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Location Type */}
                    <div>
                        <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                            Location Type
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {LOCATION_TYPES.map(locType => {
                                const Icon = locType.icon;
                                return (
                                    <button
                                        key={locType.id}
                                        type="button"
                                        onClick={() => handleChange('locationType', locType.id)}
                                        className={`p-3 rounded-lg border text-left transition ${
                                            formData.locationType === locType.id
                                                ? 'border-yellow-500 dark:bg-yellow-500/10 bg-yellow-50'
                                                : 'dark:border-slate-700 border-slate-200 dark:hover:bg-slate-800 hover:bg-slate-50'
                                        }`}
                                    >
                                        <Icon size={18} className={formData.locationType === locType.id ? 'text-yellow-500' : 'dark:text-slate-400 text-slate-500'} />
                                        <p className={`text-sm font-medium mt-1 ${formData.locationType === locType.id ? 'text-yellow-500' : 'dark:text-white text-slate-900'}`}>
                                            {locType.label}
                                        </p>
                                        <p className="text-xs dark:text-slate-500 text-slate-400 mt-0.5">
                                            {locType.description}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Location Details */}
                    {(formData.locationType === 'in_person' || formData.locationType === 'custom') && (
                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                {formData.locationType === 'in_person' ? 'Address' : 'Location/Link'}
                            </label>
                            <input
                                type="text"
                                value={formData.location}
                                onChange={(e) => handleChange('location', e.target.value)}
                                placeholder={formData.locationType === 'in_person' ? 'Enter address...' : 'Enter meeting link or details...'}
                                className="w-full px-4 py-2.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition"
                            />
                        </div>
                    )}

                    {/* Buffer Times */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                Buffer Before
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    value={formData.bufferBefore}
                                    onChange={(e) => handleChange('bufferBefore', parseInt(e.target.value) || 0)}
                                    min={0}
                                    max={120}
                                    className="w-20 px-3 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                                />
                                <span className="text-sm dark:text-slate-400 text-slate-500">min</span>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                Buffer After
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    value={formData.bufferAfter}
                                    onChange={(e) => handleChange('bufferAfter', parseInt(e.target.value) || 0)}
                                    min={0}
                                    max={120}
                                    className="w-20 px-3 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                                />
                                <span className="text-sm dark:text-slate-400 text-slate-500">min</span>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                Minimum Notice
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    value={formData.minNotice}
                                    onChange={(e) => handleChange('minNotice', parseInt(e.target.value) || 0)}
                                    min={0}
                                    className="w-20 px-3 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                                />
                                <span className="text-sm dark:text-slate-400 text-slate-500">min</span>
                            </div>
                        </div>
                    </div>

                    {/* Schedule Selection */}
                    {schedules.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                Availability Schedule
                            </label>
                            <select
                                value={formData.scheduleId}
                                onChange={(e) => handleChange('scheduleId', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition"
                            >
                                <option value="">Default schedule</option>
                                {schedules.map(schedule => (
                                    <option key={schedule.id} value={schedule.id}>
                                        {schedule.name} {schedule.is_default && '(Default)'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Options */}
                    <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.requiresConfirmation}
                                onChange={(e) => handleChange('requiresConfirmation', e.target.checked)}
                                className="w-4 h-4 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                            />
                            <div>
                                <p className="text-sm font-medium dark:text-white text-slate-900">Require confirmation</p>
                                <p className="text-xs dark:text-slate-400 text-slate-500">Bookings need manual approval</p>
                            </div>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                className="w-4 h-4 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                            />
                            <div>
                                <p className="text-sm font-medium dark:text-white text-slate-900">Active</p>
                                <p className="text-xs dark:text-slate-400 text-slate-500">Allow new bookings for this event type</p>
                            </div>
                        </label>
                    </div>

                    {/* Error Message */}
                    {errors.submit && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                            <p className="text-sm text-red-500">{errors.submit}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t dark:border-slate-800 border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg dark:text-slate-300 text-slate-600 dark:hover:bg-slate-800 hover:bg-slate-100 font-medium transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-6 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:bg-yellow-600/50 text-white rounded-lg font-medium flex items-center gap-2 transition"
                        >
                            {isSaving && <Loader2 size={16} className="animate-spin" />}
                            {isEditing ? 'Save Changes' : 'Create Event Type'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
};

export default EventTypeEditor;
